import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './HierarchicalVehicleFilter.css';
import SharedSearchInput from '../../../../../components/shared/SharedSearchInput/SharedSearchInput';

/**
 * HierarchicalVehicleFilter - Ein komplexer Filter für Fahrzeuge, gruppiert nach Kategorien.
 * Unterstützt Suche, Kategorieweise Auswahl und individuelles Toggle.
 * 
 * @param {Object} props
 * @param {Array} props.selected - Liste der gewählten Fahrzeug-IDs
 * @param {Function} props.onToggle - Callback bei Selektionsänderung
 * @param {Function} props.onAll - Callback um alle zu wählen
 * @param {Function} props.onNone - Callback um alle abzuwählen
 * @param {string} props.openDropdown - ID des aktuell offenen Dropdowns
 * @param {Function} props.setOpenDropdown - Callback zum Öffnen/Schließen
 * @param {Object} props.groupedVehicles - Fahrzeuge gruppiert nach Category { 'Kat': [Vehicles] }
 * @param {number} props.totalCount - Gesamtanzahl der Fahrzeuge (für Label)
 */
const HierarchicalVehicleFilter = ({ 
    selected, 
    onToggle, 
    onAll, 
    onNone, 
    openDropdown, 
    setOpenDropdown, 
    groupedVehicles, 
    totalCount 
}) => {
    const isOpen = openDropdown === 'vehicles';
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState(new Set());
    const inputRef = useRef(null);

    // Auto-focus und Reset bei Öffnen
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    // Filter-Logik für Suche
    const filteredGroups = useMemo(() => {
        if (!searchTerm) return groupedVehicles;
        const result = {};
        const lowered = searchTerm.toLowerCase();

        Object.entries(groupedVehicles).forEach(([cat, vehicles]) => {
            const matches = vehicles.filter(v =>
                (v.license_plate || '').toLowerCase().includes(lowered) ||
                (v.internal_name || '').toLowerCase().includes(lowered)
            );
            if (matches.length > 0) {
                result[cat] = matches;
            }
        });
        return result;
    }, [groupedVehicles, searchTerm]);

    // Automatisch aufklappen bei Suche
    useEffect(() => {
        if (searchTerm) {
            setExpanded(new Set(Object.keys(filteredGroups)));
        }
    }, [filteredGroups, searchTerm]);

    const handleSelectFiltered = () => {
        const allFilteredIds = Object.values(filteredGroups).flat().map(v => v.id);
        const next = [...new Set([...selected, ...allFilteredIds])];
        onToggle(next);
    };

    const handleDeselectFiltered = () => {
        const filteredIds = new Set(Object.values(filteredGroups).flat().map(v => v.id));
        const next = selected.filter(id => !filteredIds.has(id));
        onToggle(next);
    };

    const toggleExpanded = (cat) => {
        const next = new Set(expanded);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        setExpanded(next);
    };

    const toggleCategory = (cat, catVehicles) => {
        const catIds = catVehicles.map(v => v.id);
        const allSelected = catIds.every(id => selected.includes(id));
        let next;
        if (allSelected) {
            next = selected.filter(id => !catIds.includes(id));
        } else {
            next = [...new Set([...selected, ...catIds])];
        }
        onToggle(next);
    };

    const isAllSelected = selected.length === totalCount && totalCount > 0;
    const isIndeterminate = selected.length > 0 && !isAllSelected;

    return (
        <div className={`export-filter-dropdown ${isOpen ? 'open' : ''}`}>
            <button
                className={`export-filter-btn ${isIndeterminate || isAllSelected ? 'has-selection' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : 'vehicles'); }}
            >
                <span className="export-filter-label">Fahrzeuge</span>
                <span className="export-filter-count">
                    {selected.length === totalCount ? 'Alle' : selected.length}
                </span>
                <span className="export-filter-chevron">▼</span>
            </button>
            
            {isOpen && (
                <div className="export-dropdown-menu">
                    <div className="export-dropdown-search">
                        <SharedSearchInput
                            ref={inputRef}
                            placeholder="Fahrzeug suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClear={() => setSearchTerm('')}
                        />
                    </div>
                    <div className="export-dropdown-list hierarchical">
                        {Object.keys(filteredGroups).length === 0 ? (
                            <div className="export-dropdown-empty">Keine Treffer</div>
                        ) : (
                            Object.entries(filteredGroups).map(([cat, catVehicles]) => {
                                const selectedInCat = catVehicles.filter(v => selected.includes(v.id));
                                const isAll = selectedInCat.length === catVehicles.length;
                                const isIndeterminate = selectedInCat.length > 0 && !isAll;
                                const isExpanded = expanded.has(cat);

                                return (
                                    <div key={cat} className="export-hierarchy-group">
                                        <div className="export-hierarchy-parent" onClick={() => toggleCategory(cat, catVehicles)}>
                                            <div
                                                className={`export-hierarchy-expander ${isExpanded ? 'open' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); toggleExpanded(cat); }}
                                            >
                                                ▶
                                            </div>
                                            <div className="export-hierarchy-checkbox-wrap">
                                                <div className={`custom-checkbox mini ${isAll ? 'checked' : ''} ${isIndeterminate ? 'indeterminate' : ''}`}>
                                                    {isAll ? '✓' : isIndeterminate ? '—' : ''}
                                                </div>
                                                <span className="export-hierarchy-label">{cat} ({catVehicles.length})</span>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="export-hierarchy-children">
                                                {catVehicles.map(v => {
                                                    const isSelected = selected.includes(v.id);
                                                    return (
                                                        <div
                                                            key={v.id}
                                                            className="export-hierarchy-item"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const next = isSelected
                                                                    ? selected.filter(id => id !== v.id)
                                                                    : [...selected, v.id];
                                                                onToggle(next);
                                                            }}
                                                        >
                                                            <div className={`custom-checkbox mini ${isSelected ? 'checked' : ''}`}>
                                                                {isSelected ? '✓' : ''}
                                                            </div>
                                                            <span className="export-hierarchy-sublabel">
                                                                {v.license_plate || v.internal_name}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            }))}
                    </div>
                    <div className="export-dropdown-actions">
                        <button className="export-action-btn" onClick={searchTerm ? handleSelectFiltered : onAll}>
                            {searchTerm ? 'Diese Treffer' : 'Alle'}
                        </button>
                        <button className="export-action-btn" onClick={searchTerm ? handleDeselectFiltered : onNone}>
                            {searchTerm ? 'Entfernen' : 'Keine'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

HierarchicalVehicleFilter.propTypes = {
    selected: PropTypes.array.isRequired,
    onToggle: PropTypes.func.isRequired,
    onAll: PropTypes.func.isRequired,
    onNone: PropTypes.func.isRequired,
    openDropdown: PropTypes.string,
    setOpenDropdown: PropTypes.func.isRequired,
    groupedVehicles: PropTypes.object.isRequired,
    totalCount: PropTypes.number.isRequired
};

export default HierarchicalVehicleFilter;
